import React, { useState } from 'react';
import { Stack, Text, Divider } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faVideo, faMusic } from '@fortawesome/free-solid-svg-icons';
import { AudioTrack } from '../types';
import { SoundtrackExportButton } from './SoundtrackExportButton';
import { SoundtrackDownloadSection } from './SoundtrackDownloadSection';
import { useVideoProject } from '../hooks/useVideoProject';
import { SoundtrackStatus } from '../../../services/soundtrackService';

interface SoundtrackManagerProps {
  projectId: string;
  token: string | null;
  tracks: AudioTrack[];
  masterVolume: number;
  generateMixedAudio: () => Promise<Blob>;
  onSoundtrackReady?: (videoUrl: string) => void;
}

export const SoundtrackManager: React.FC<SoundtrackManagerProps> = ({
  projectId,
  token,
  tracks,
  masterVolume,
  generateMixedAudio,
  onSoundtrackReady
}) => {
  const [currentJobId, setCurrentJobId] = useState<string | undefined>();
  
  const {
    isVideoProject,
    projectData,
    soundtrackStatus,
    isLoading,
    error,
    updateSoundtrackStatus
  } = useVideoProject({ projectId, token });

  const handleExportStart = () => {
    console.log('Soundtrack export started');
  };

  const handleExportComplete = (jobId: string) => {
    setCurrentJobId(jobId);
    console.log('Soundtrack export job started:', jobId);
  };

  const handleExportError = (errorMessage: string) => {
    console.error('Soundtrack export error:', errorMessage);
    setCurrentJobId(undefined);
  };

  const handleStatusUpdate = (status: SoundtrackStatus) => {
    updateSoundtrackStatus(status);
    
    // Clear job ID when completed or failed
    if (status.status === 'completed' || status.status === 'failed') {
      setCurrentJobId(undefined);
    }
    
    // Notify parent when soundtrack video is ready
    if (status.status === 'completed' && status.soundtrackVideoUrl && onSoundtrackReady) {
      onSoundtrackReady(status.soundtrackVideoUrl);
    }
  };

  // Don't render anything if not a video project
  if (!isVideoProject || error) {
    return null;
  }

  // Show loading state
  if (isLoading) {
    return null;
  }

  // Get video URL with fallback priority
  const videoUrl = projectData?.captionedVideoUrl || 
                   projectData?.generatedVideoUrl || 
                   projectData?.originalVideoUrl;

  return (
    <>
      {/* Export Button - NO automatic processing state from backend */}
      <SoundtrackExportButton
        projectId={projectId}
        tracks={tracks}
        masterVolume={masterVolume}
        generateMixedAudio={generateMixedAudio}
        videoUrl={videoUrl} // NEW: Pass video URL
        onExportStart={handleExportStart}
        onExportComplete={handleExportComplete}
        onExportError={handleExportError}
        disabled={tracks.length === 0}
      />

      {/* Download Section */}
      <SoundtrackDownloadSection
        projectId={projectId}
        jobId={currentJobId}
        onStatusUpdate={handleStatusUpdate}
      />
    </>
  );
};
